import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PostDetail, PostListResponse } from '../types';
import { api } from './client';
import { qk } from './keys';

export interface PostsListParams {
  locale: string;
  q?: string;
  category?: string;
  tag?: string;
  author?: string;
  from?: string;
  to?: string;
  sort?: 'newest' | 'oldest' | 'relevance';
  page?: number;
  pageSize?: number;
  type?: 'post' | 'page';
  enabled?: boolean;
}

export function usePosts(params: PostsListParams) {
  return useQuery({
    queryKey: qk.posts(params),
    enabled: params.enabled !== false,
    queryFn: () =>
      api.get<PostListResponse>(params.type === 'page' ? '/pages' : '/posts', {
        locale: params.locale,
        q: params.q,
        category: params.category,
        tag: params.tag,
        author: params.author,
        from: params.from,
        to: params.to,
        sort: params.sort,
        page: params.page,
        pageSize: params.pageSize,
      }),
  });
}

export function usePost(slug: string | null, locale: string, type: 'post' | 'page' = 'post') {
  return useQuery({
    queryKey: qk.post(slug ?? '', locale),
    enabled: !!slug,
    queryFn: () =>
      api.get<PostDetail>(
        `${type === 'page' ? '/pages' : '/posts'}/${encodeURIComponent(slug ?? '')}`,
        {
          locale,
        },
      ),
  });
}

export function useCategories(locale: string) {
  return useQuery({
    queryKey: qk.categories(locale),
    queryFn: () =>
      api.get<{ items: { name: string; slug: string; count: number }[] }>('/categories', {
        locale,
      }),
  });
}

export function useTags(locale: string) {
  return useQuery({
    queryKey: qk.tags(locale),
    queryFn: () =>
      api.get<{ items: { name: string; slug: string; count: number }[] }>('/tags', { locale }),
  });
}

export function useAuthors(locale: string) {
  return useQuery({
    queryKey: qk.authors(locale),
    queryFn: () =>
      api.get<{ items: { name: string; slug: string; count: number }[] }>('/authors', { locale }),
  });
}

export function useTrending(window: '1h' | '6h' | '24h' | '7d' | '30d', locale: string) {
  return useQuery({
    queryKey: qk.trending(window, locale),
    queryFn: () =>
      api.get<{
        window: string;
        windowFromDate: string;
        items: { id: string; slug: string; title: string; score: number }[];
        categories: { name: string; slug: string; postCount: number }[];
        tags: { name: string; slug: string; postCount: number }[];
      }>('/trending', { window, locale }),
  });
}

export function useBulkDeletePosts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ deleted: number }>('/admin/posts/bulk-delete', { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['posts'] }),
  });
}
